import Channel from '@/Enums/Channel.enum';
import CreativeFileStatus from '@/Enums/Creatives/CreativeFileStatus.enum';
import CreativeType from '@/Enums/Creatives/CreativeType.enum';
import UploadMode from '@/Enums/Creatives/UploadMode.enum';
import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import { IAdvertiserControlOption } from '@/Interfaces/Advertiser.interface';
import { ICreative, ICreativeFormData } from '@/Interfaces/Creatives/Creative.interface';
import CreativeService from '@/Services/Creative/Creative.service';
import CreativeTypeService from '@/Services/Creative/CreativeType.service';
import ConfirmationModal from '@/components/Modal/ConfirmationModal';
import CloseButton from '@/components/Modal/common/CloseButton';
import ModalBody from '@/components/Modal/common/ModalBody';
import InputSkeleton from '@/components/Skeleton/InputSkeleton';
import { CREATIVE_TYPES_ICONS, CREATIVE_TYPES_TITLES } from '@/constants/creativeLibrary';
import { creativeFormDataToApiStructure, creativeToFormData } from '@/helpers/creativeLibrary';
import { getFieldErrorProps } from '@/helpers/form';
import AssociateCreativesModal from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/Creatives/Modal';
import CreativesModalButtons from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/Creatives/Modal/components/CreativesModalButtons';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectMeScreenAccess } from '@/store/selectors/me.selectors';
import { selectSharedApiCreativeSizes } from '@/store/selectors/sharedApi.selectors';
import { bulkCreateCreatives, createCreative, getAdvertisers, updateCreative } from '@/store/slices/creativeSlice';
import { getCreativeSizes } from '@/store/slices/sharedApi.slice';
import type {
  BulkCreativeType,
  CreativeCreatePayload,
  CreativeUpdatePayload,
  InlineCreativeType,
} from '@/types/creativeLibrary';
import { URL_RULES } from '@/utils/validationRules';
import { Autocomplete, Button, FormLabel, Modal, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { HttpStatusCode, isAxiosError } from 'axios';
import { type ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import ClickTagWarning from './components/ClickTagWarning';
import FilePreview from './components/FilePreview';
import FileUpload from './components/FileUpload';
import MapItemButton from './components/MapItemButton';
import PixelsInput from './components/PixelsInput';
import TagFileUpload from './components/TagFileUpload';
import ZipFileUpload from './components/ZipFileUpload';
import { defaultFormData } from './constants';

const BULK_CREATIVE_TYPES: CreativeType[] = [CreativeType.VIDEO, CreativeType.DISPLAY, CreativeType.VIDEO_VAST];

export interface ICreativeFormModalDefaults {
  advertiser: IAdvertiserControlOption;
  types: CreativeType[];
}

interface ICreativeFormModalProps {
  open: boolean;
  handleClose: (refresh?: boolean) => void;
  creativeData?: ICreative;
  defaults?: ICreativeFormModalDefaults;
  onBulkAssign?: (creative: ICreative) => void;
  onDelete?: (creative: ICreative) => void;
}

const CreativeFormModal = memo(
  ({ open, handleClose, creativeData, defaults, onBulkAssign, onDelete }: ICreativeFormModalProps) => {
    const {
      getValues,
      control,
      formState: { isValid },
      setValue,
      reset,
      resetField,
    } = useForm<ICreativeFormData>({
      defaultValues: defaultFormData,
      mode: 'onChange',
    });

    const dispatch = useAppDispatch();
    const advertisers = useAppSelector<IAdvertiserControlOption[]>(({ creative }) =>
      defaults?.advertiser ? [defaults.advertiser] : creative.advertisers,
    );
    const { creativeSizes, loadingCreativeSizes } = useAppSelector(selectSharedApiCreativeSizes);
    const advertisersLoading = useAppSelector<boolean>(({ creative }) => !!creative.advertisersRequestId);
    const readOnly = useAppSelector(selectMeScreenAccess(ScreenKey.CREATIVE_LIBRARY));

    const [loading, setLoading] = useState<boolean>(false);
    const [mode, setMode] = useState<UploadMode>(UploadMode.SINGLE);
    const [bulkZipFiles, setBulkZipFiles] = useState<File[]>([]);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isChooseExistingOpen, setIsChooseExistingOpen] = useState(false);
    const [selectedCompanionCreative, setSelectedCompanionCreative] = useState<ICreative | null>(null);

    const companionFileInputRef = useRef<HTMLInputElement | null>(null);

    const isBulkMode = mode === UploadMode.BULK && !creativeData;

    const [watchFormat, watchFile, watchTagContent, watchPixels] = useWatch({
      control,
      name: ['format', 'file', 'tagContent', 'pixels'],
    });

    const [
      watchCompanionImage,

      watchAdvertiser,
      watchCompanionFileUrl,

      watchCompanionThumbnail,
    ] = useWatch({
      control,
      name: ['companionAssetImage', 'advertiser', 'companionAssetFileUrl', 'companionAssetThumbnail'],
    });

    const isAudioType = useMemo(
      () => [CreativeType.AUDIO, CreativeType.AUDIO_VAST].includes(watchFormat),
      [watchFormat],
    );

    const uploadedCompanionPreviewSrc = useMemo(() => {
      if (!watchCompanionImage) return null;
      return URL.createObjectURL(watchCompanionImage);
    }, [watchCompanionImage]);

    useEffect(() => {
      return () => {
        if (uploadedCompanionPreviewSrc) {
          URL.revokeObjectURL(uploadedCompanionPreviewSrc);
        }
      };
    }, [uploadedCompanionPreviewSrc]);

    const existingCompanionPreviewSrc = watchCompanionThumbnail || null;
    const companionImagePreviewSrc = existingCompanionPreviewSrc || uploadedCompanionPreviewSrc || null;

    const resetCompanionImageState = useCallback(() => {
      setSelectedCompanionCreative(null);
      setValue('companionAssetImage', null);
      setValue('companionAssetFileUrl', '');
      setValue('companionAssetThumbnail', '');
    }, [setValue]);

    const setCompanionImage = useCallback(
      (file: File) => {
        setSelectedCompanionCreative(null);
        setValue('companionAssetFileUrl', '');
        setValue('companionAssetThumbnail', '');
        setValue('companionAssetImage', file);
      },
      [setValue],
    );

    const handleCompanionNativeFileChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setCompanionImage(file);
        e.target.value = '';
      },
      [setCompanionImage],
    );

    const handleOpenCompanionFileDialog = useCallback(() => {
      companionFileInputRef.current?.click();
    }, []);

    useEffect(() => {
      if (open && !advertisers.length) {
        dispatch(getAdvertisers());
      }
    }, [dispatch, advertisers, open]);

    useEffect(() => {
      if (open) {
        dispatch(getCreativeSizes());
      }
    }, [dispatch, open]);

    useEffect(() => {
      if (defaults?.advertiser) {
        reset({
          ...defaultFormData,
          advertiser: defaults.advertiser,
          format: defaults.types[0],
        });
        setSelectedCompanionCreative(null);
      }
    }, [defaults, reset]);

    useEffect(() => {
      if (creativeData) {
        reset(creativeToFormData(creativeData, creativeSizes));
        setSelectedCompanionCreative(null);
      }
    }, [creativeData, creativeSizes, reset]);

    const handleModeChange = useCallback(
      (_: unknown, newMode: UploadMode | null) => {
        if (!newMode) return;

        setMode(newMode);
        setBulkZipFiles([]);
        setSelectedCompanionCreative(null);

        reset({
          ...defaultFormData,
          ...(defaults?.advertiser ? { advertiser: defaults.advertiser } : {}),
          format:
            newMode === UploadMode.BULK
              ? defaults?.types
                ? BULK_CREATIVE_TYPES.filter(t => defaults.types.includes(t))[0]
                : BULK_CREATIVE_TYPES[0]
              : (defaults?.types?.[0] ?? CreativeType.VIDEO),
        });
      },
      [reset, defaults],
    );

    const handleFormatChange = useCallback(
      ({ target: { id } }: ChangeEvent<HTMLInputElement>) => {
        setValue('format', id as CreativeType);
        resetField('clickUrl');
        resetField('tagContent');
        resetField('file');
        resetField('creativeSize');
        resetCompanionImageState();

        if (isBulkMode) setBulkZipFiles([]);
      },
      [setValue, resetField, isBulkMode, resetCompanionImageState],
    );

    const setUploadedFile = useCallback(
      (file: File) => {
        setValue('file', file);
      },
      [setValue],
    );

    const handleTagContentChange = useCallback(
      ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
        setValue('tagContent', value);
      },
      [setValue],
    );

    const handlePixelsChange = useCallback(
      (pixels: string[]) => {
        setValue('pixels', pixels);
      },
      [setValue],
    );

    const _handleClose = useCallback(
      (refresh?: boolean) => {
        reset(defaultFormData);
        setMode(UploadMode.SINGLE);
        setBulkZipFiles([]);
        setSelectedCompanionCreative(null);
        handleClose(refresh);
      },
      [reset, handleClose],
    );

    const handleSave = useCallback(async () => {
      const formData = getValues();

      setLoading(true);

      try {
        if (isBulkMode && bulkZipFiles.length) {
          const basePayload = {
            advertiserId: formData.advertiser!.value,
            type: formData.format as BulkCreativeType,
            clickUrl: formData.clickUrl || undefined,
            pixels: formData.pixels?.length ? formData.pixels : undefined,
          };

          await Promise.all(bulkZipFiles.map(file => dispatch(bulkCreateCreatives({ ...basePayload, file })).unwrap()));
          _handleClose(true);
        } else {
          const data = creativeFormDataToApiStructure(formData, creativeData?.id) as
            | CreativeCreatePayload
            | CreativeUpdatePayload
            | Record<string, unknown>;

          if (creativeData?.id) {
            await dispatch(updateCreative(data as CreativeUpdatePayload)).unwrap();
          } else {
            await dispatch(createCreative(data as CreativeCreatePayload)).unwrap();
          }

          toast.success(`Creative ${creativeData?.id ? 'updated' : 'created'} successfully`);
          _handleClose(!creativeData?.id);
        }
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === HttpStatusCode.Forbidden) {
          toast.error("You don't have permission to add creatives for this advertiser.");
        } else {
          const message = isAxiosError(err) ? err.response?.data?.message : undefined;
          toast.error(`Error creating creative: ${message || (err as Error).message}`);
        }
      }

      setLoading(false);
    }, [getValues, creativeData?.id, dispatch, _handleClose, isBulkMode, bulkZipFiles, isAudioType]);

    const isSaveDisabled = useMemo(() => {
      if (isBulkMode) {
        return !bulkZipFiles.length;
      }

      if (CreativeTypeService.isInline(watchFormat)) {
        return !watchFile;
      }

      if (CreativeTypeService.isWrapper(watchFormat) || watchFormat === CreativeType.DISPLAY_TAG) {
        return !watchTagContent;
      }

      return false;
    }, [isBulkMode, bulkZipFiles, watchFile, watchFormat, watchTagContent]);

    const showCreativeSizeControl = useMemo(() => CreativeTypeService.hasSize(watchFormat), [watchFormat]);

    const showTagClickWarning = useMemo(() => {
      if (!showCreativeSizeControl || !watchTagContent?.trim() || watchFormat === CreativeType.DISPLAY_TAG) {
        return false;
      }

      return !CreativeService.hasClickUrlMacro(watchTagContent);
    }, [showCreativeSizeControl, watchTagContent, watchFormat]);

    return (
      <Modal open={open}>
        <ModalBody className='!w-[740px]'>
          <p className='pl-4 pt-2 mb-6 text-xl font-semibold'>{creativeData ? 'Edit Creative' : 'Add New Creative'}</p>
          <div className='absolute right-[24px] top-[24px] flex items-center gap-2'>
            {creativeData && creativeData.beeswaxId && !readOnly && onBulkAssign && (
              <Button
                variant='contained'
                size='small'
                sx={{ backgroundColor: '#1B4332', '&:hover': { backgroundColor: '#143728' }, textTransform: 'none' }}
                onClick={() => onBulkAssign(creativeData)}
              >
                Bulk Assign
              </Button>
            )}
            <CloseButton onClick={_handleClose} />
          </div>
          <form className='mx-4 flex flex-col gap-y-4'>
            {!creativeData && (
              <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size='small'>
                <ToggleButton value={UploadMode.SINGLE}>Single</ToggleButton>
                <ToggleButton value={UploadMode.BULK}>Bulk</ToggleButton>
              </ToggleButtonGroup>
            )}
            {!isBulkMode && (
              <div className='flex flex-col w-full'>
                <FormLabel className='text-sm font-semibold mb-2' htmlFor='creative-name-input'>
                  Creative Name*
                </FormLabel>
                {loading ? (
                  <InputSkeleton />
                ) : (
                  <Controller
                    name='name'
                    rules={{ required: { value: !isBulkMode, message: 'Creative Name is required' } }}
                    control={control}
                    disabled={readOnly}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder='Enter Creative Name'
                        {...getFieldErrorProps(fieldState)}
                        id='creative-name-input'
                      />
                    )}
                  />
                )}
              </div>
            )}
            {!isBulkMode && (
              <div className='flex flex-col w-full'>
                <FormLabel className='text-sm font-semibold mb-2' htmlFor='creative-id-input'>
                  Creative External ID
                </FormLabel>
                {loading ? (
                  <InputSkeleton />
                ) : (
                  <Controller
                    name='alternativeId'
                    control={control}
                    disabled={readOnly}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder='Enter Alternative ID'
                        {...getFieldErrorProps(fieldState)}
                        id='creative-id-input'
                      />
                    )}
                  />
                )}
              </div>
            )}
            <div className='flex flex-col w-full'>
              <FormLabel className='text-sm font-semibold mb-2'>Format</FormLabel>
              <div className='flex flex-wrap gap-x-2 gap-y-2'>
                {(isBulkMode
                  ? defaults?.types
                    ? BULK_CREATIVE_TYPES.filter(t => defaults.types.includes(t))
                    : BULK_CREATIVE_TYPES
                  : (defaults?.types ?? Object.values(CreativeType))
                )
                  .filter(type => type !== CreativeType.YOUTUBE_LINK)
                  .map(type => (
                    <MapItemButton
                      key={type}
                      disabled={loading || !!creativeData || readOnly}
                      label={CREATIVE_TYPES_TITLES[type]}
                      Icon={CREATIVE_TYPES_ICONS[type]}
                      checked={watchFormat === type}
                      onChange={handleFormatChange}
                      id={type}
                    />
                  ))}
              </div>
            </div>
            <div className='flex flex-col w-full'>
              <FormLabel className='text-sm font-semibold mb-2' htmlFor='advertiser-select'>
                Advertiser*
              </FormLabel>
              {loading ? (
                <InputSkeleton />
              ) : (
                <Controller
                  name='advertiser'
                  rules={{ required: { value: true, message: 'Advertiser is required' } }}
                  control={control}
                  disabled={readOnly}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      {...field}
                      renderInput={props => (
                        <TextField
                          {...props}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontWeight: '400',
                            },
                          }}
                          placeholder='Select Advertiser'
                          {...getFieldErrorProps(fieldState)}
                        />
                      )}
                      getOptionLabel={option => `${option.label} (ID: ${option.beeswaxId ?? 'N/A'})`}
                      getOptionKey={option => option.value}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      onChange={(_, value) => field.onChange(value)}
                      options={advertisers}
                      loading={advertisersLoading}
                      disabled={!!defaults?.advertiser || readOnly}
                      id='advertiser-select'
                    />
                  )}
                />
              )}
            </div>
            {CreativeTypeService.hasClickUrl(watchFormat) && (
              <div className='flex flex-col w-full'>
                <FormLabel className='text-sm font-semibold mb-2' htmlFor='click-url-input'>
                  Click URL*
                </FormLabel>
                {loading ? (
                  <InputSkeleton />
                ) : (
                  <Controller
                    name='clickUrl'
                    rules={URL_RULES}
                    control={control}
                    disabled={readOnly}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder='Enter Click URL'
                        {...getFieldErrorProps(fieldState)}
                        id='click-url-input'
                      />
                    )}
                  />
                )}
              </div>
            )}
            <div className='flex flex-col w-full'>
              {isBulkMode ? (
                <>
                  <FormLabel className='text-sm font-semibold mb-2'>
                    {watchFormat === CreativeType.VIDEO_VAST ? 'Upload File' : 'Upload ZIP'}
                  </FormLabel>
                  {watchFormat === CreativeType.VIDEO_VAST ? (
                    <TagFileUpload
                      key={watchFormat}
                      loading={loading}
                      setUploadedFiles={setBulkZipFiles}
                      disabled={readOnly}
                    />
                  ) : (
                    <ZipFileUpload
                      key={watchFormat}
                      loading={loading}
                      setUploadedFiles={setBulkZipFiles}
                      disabled={readOnly}
                    />
                  )}
                </>
              ) : CreativeTypeService.isInline(watchFormat) ? (
                <>
                  <FormLabel className='text-sm font-semibold mb-2'>
                    {creativeData ? 'Preview' : 'Upload'} Creative
                  </FormLabel>
                  {creativeData &&
                  [CreativeFileStatus.UPLOADED, CreativeFileStatus.STORED_IN_BEESWAX].includes(
                    creativeData.fileStatus,
                  ) ? (
                    <FilePreview
                      format={watchFormat as InlineCreativeType}
                      src={creativeData.file!}
                      className='max-h-[214px]'
                    />
                  ) : (
                    <FileUpload
                      key={watchFormat}
                      format={watchFormat as InlineCreativeType}
                      setUploadedFile={setUploadedFile}
                      loading={loading}
                      failed={creativeData?.fileStatus === CreativeFileStatus.FAILED}
                      disabled={readOnly}
                    />
                  )}
                </>
              ) : (
                <>
                  <FormLabel className='text-sm font-semibold mb-2' htmlFor='tag-content-textarea'>
                    Enter {CreativeTypeService.isWrapper(watchFormat) ? 'VAST' : 'Display'} Tag
                  </FormLabel>
                  <textarea
                    key={watchFormat}
                    className='outline-[#1B4332] border border-[#0000001a] hover:border-[#1B4332] rounded-md p-2.5 resize-none min-h-[200px] max-h-[200px]'
                    disabled={loading || readOnly}
                    value={watchTagContent || ''}
                    onChange={handleTagContentChange}
                    id='tag-content-textarea'
                  />
                </>
              )}
            </div>
            {!isBulkMode && CreativeTypeService.isWrapper(watchFormat) && (
              <div className='flex flex-col w-full'>
                <FormLabel className='text-sm font-semibold mb-2' htmlFor='creative-duration-input'>
                  Duration (seconds)
                </FormLabel>
                <Controller
                  name='duration'
                  control={control}
                  disabled={readOnly}
                  rules={{ min: { value: 1, message: 'Duration must be greater than 0' } }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type='number'
                      fullWidth
                      placeholder="We'll try to detect this automatically from the tag"
                      helperText={fieldState.error?.message ?? "Only needed if we can't auto-detect it"}
                      error={!!fieldState.error}
                      id='creative-duration-input'
                    />
                  )}
                />
              </div>
            )}
            {showTagClickWarning ? <ClickTagWarning /> : null}
            {showCreativeSizeControl ? (
              <div>
                <FormLabel className='text-sm font-semibold mb-2' htmlFor='creative-sizes-select'>
                  Creative Size*
                </FormLabel>
                <Controller
                  name='creativeSize'
                  rules={{ required: { value: true, message: 'Creative Size is required' } }}
                  control={control}
                  disabled={readOnly}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      {...field}
                      renderInput={props => (
                        <TextField
                          {...props}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontWeight: '400',
                            },
                          }}
                          placeholder='Select Creative Size'
                          {...getFieldErrorProps(fieldState)}
                        />
                      )}
                      getOptionKey={option => option.value}
                      isOptionEqualToValue={(option, value) => option.value === value?.value}
                      onChange={(_, value) => field.onChange(value)}
                      options={creativeSizes}
                      disabled={loadingCreativeSizes || loading || readOnly}
                      id='creative-sizes-select'
                    />
                  )}
                />
              </div>
            ) : null}
            {isAudioType && (
              <div className='flex flex-col w-full gap-y-3'>
                <FormLabel className='text-sm font-semibold'>Companion Asset</FormLabel>
                <div className='border border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center gap-4'>
                  <input
                    ref={companionFileInputRef}
                    type='file'
                    accept='image/png,image/jpeg,image/jpg,image/gif'
                    className='hidden'
                    onChange={handleCompanionNativeFileChange}
                  />
                  <div className='flex gap-4'>
                    <Button variant='outlined' onClick={handleOpenCompanionFileDialog} disabled={loading || readOnly}>
                      Upload Files
                    </Button>
                    <Button
                      variant='outlined'
                      onClick={() => setIsChooseExistingOpen(true)}
                      disabled={loading || readOnly || !watchAdvertiser}
                    >
                      Choose Existing
                    </Button>
                  </div>
                  {companionImagePreviewSrc ? (
                    <div className='w-full flex flex-col gap-3 items-center'>
                      <img
                        src={companionImagePreviewSrc}
                        alt={selectedCompanionCreative?.name || 'Companion preview'}
                        className='max-h-[214px] object-contain rounded'
                      />
                      {watchCompanionFileUrl && !watchCompanionImage ? (
                        <div className='text-sm text-gray-500 text-center'>Existing companion asset selected</div>
                      ) : watchCompanionImage ? (
                        <div className='text-sm text-gray-500 text-center'>
                          Uploaded image: {watchCompanionImage.name}
                        </div>
                      ) : null}
                      <Button
                        size='small'
                        variant='outlined'
                        color='error'
                        onClick={resetCompanionImageState}
                        disabled={loading || readOnly}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <FileUpload
                      key='companion-asset-image'
                      format={CreativeType.DISPLAY}
                      setUploadedFile={setCompanionImage}
                      loading={loading}
                      disabled={readOnly}
                    />
                  )}
                </div>
              </div>
            )}
            <AssociateCreativesModal
              open={isChooseExistingOpen}
              handleClose={() => setIsChooseExistingOpen(false)}
              associatedCreatives={[]}
              channel={Channel.Display}
              selectionMode='single'
              allowedTypes={[CreativeType.DISPLAY]}
              handleAssociateCreatives={creatives => {
                const selected = creatives[0];
                if (!selected?.file) return;
                setSelectedCompanionCreative(selected);
                setValue('companionAssetImage', null);
                setValue('companionAssetFileUrl', selected.file);
                setValue('companionAssetThumbnail', selected.thumbnail || '');
                setIsChooseExistingOpen(false);
              }}
            />
            <PixelsInput
              pixels={watchPixels}
              handlePixelsChange={handlePixelsChange}
              control={control}
              disabled={readOnly}
            />
            <div className='flex justify-between items-end'>
              {creativeData && onDelete && !readOnly && creativeData.fileStatus === CreativeFileStatus.FAILED ? (
                <Button
                  variant='outlined'
                  color='error'
                  onClick={() => setIsDeleteConfirmOpen(true)}
                  sx={{ height: '48px' }}
                >
                  Delete
                </Button>
              ) : (
                <div />
              )}
              <CreativesModalButtons
                handleClose={_handleClose}
                handleSave={handleSave}
                loading={loading}
                saveButtonTitle={isBulkMode ? 'Upload' : creativeData ? 'Save' : 'Create'}
                saveButtonLoadingTitle={isBulkMode ? 'Uploading' : creativeData ? 'Saving' : 'Creating'}
                isSaveDisabled={!isValid || isSaveDisabled || readOnly}
              />
            </div>
          </form>

          {creativeData && onDelete && (
            <ConfirmationModal
              open={isDeleteConfirmOpen}
              onClose={() => setIsDeleteConfirmOpen(false)}
              onConfirm={() => {
                setIsDeleteConfirmOpen(false);
                onDelete(creativeData);
              }}
              text='Are you sure you want to delete this creative? This action cannot be undone.'
              confirmButtonText='Delete'
            />
          )}
        </ModalBody>
      </Modal>
    );
  },
);

export default CreativeFormModal;
